import React, { useCallback } from 'react';

import { useTranslation } from 'providers/GILT.provider';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';
import randomString from 'utils/values/randomString.util';

import BWContent from 'booking/components/common/BWContent';
import ClientContactInfo from 'clientProfile/components/BasicProfile/ClientContactInfo';
import ConsultationForm from 'clientProfile/components/BasicProfile/ConsultationForm.component';
import Details from 'clientProfile/components/BasicProfile/Details.component';
import PatchNotes from 'clientProfile/components/BasicProfile/PatchNotes';
import StoppedSms from 'clientProfile/components/common/StoppedSms.component';

const L = logger('BasicProfile'); // eslint-disable-line no-unused-vars

const BasicProfile = ({
  bookingStatusReport,
  client,
  clientDepositData,
  clientTags,
  cssCheckForFeatureData,
  goToAddCredit,
  goToEditDetails,
  goToPatchView,
  goToViewDeposits,
  remainingHistoryBookings,
  saveClient,
  isSavingSms,
  onToggleSms,
  onToggleMarketing,
}) => {
  const { t } = useTranslation();
  const hasNotifications = fromAnyToBool(client?.notifications);
  const requestedOptOut = fromAnyToBool(client?.requested_opt_out);
  const stoppedSms = fromAnyToBool(client?.stopped_sms);

  const headerId = `sms-reminders-${randomString()}`;

  const displayText = isSavingSms
    ? t('composite.clientProfile.savingSms')
    : hasNotifications
      ? t('composite.clientProfile.remindersOn')
      : t('composite.clientProfile.remindersOff');

  const handleClickSms = useCallback(() => {
    if (isSavingSms) return;
    call('CheckBoxContainer.onToggle', onToggleSms, !hasNotifications);
  }, [isSavingSms, onToggleSms, hasNotifications]);

  const handleToggleMarketing = useCallback(() => {
    call('CheckBoxContainer.onToggleMarketing', onToggleMarketing, !requestedOptOut);
  }, [onToggleMarketing, requestedOptOut]);

  return (
    <div>
      <BWContent>
        <Details
          client={client}
          clientTags={clientTags}
          goToEditDetails={goToEditDetails}
          goToPatchView={goToPatchView}
          bookingStatusReport={bookingStatusReport}
          clientDepositData={clientDepositData}
          goToViewDeposits={goToViewDeposits}
          goToAddCredit={goToAddCredit}
          remainingHistoryBookings={remainingHistoryBookings}
        />
      </BWContent>

      <BWContent additionalClass="space border-t border-b">
        <ClientContactInfo client={client} goToEditDetails={goToEditDetails} />
      </BWContent>

      <BWContent additionalClass="space">
        <PatchNotes client={client} goToPatchView={goToPatchView} />

        <ConsultationForm onSave={saveClient} client={client} />

        {stoppedSms ? (
          <StoppedSms />
        ) : (
          <>
            <div data-bem="BasicProfile__sms-check-box" className="client-information block cfix">
              <span className="font-16">{t('composite.clientProfile.remindersTitle')}</span>
              <span className="font-16" style={{ opacity: 0.4 }}>
                {displayText}
              </span>

              <div data-bem="BasicProfile__checkbox">
                <div className="notifications">
                  <input
                    type="checkbox"
                    className="checkbox-switch"
                    id={headerId}
                    checked={hasNotifications}
                    onChange={handleClickSms}
                  />
                  <label htmlFor={headerId} />
                </div>
                <div />
              </div>
            </div>

            <div className="client-information block cfix">
              <span className="font-16">{t('composite.clientProfile.smsMarketingTitle')}</span>
              <span className="font-16" style={{ opacity: 0.4 }}>
                {t('composite.clientProfile.clientPreference')}
              </span>
              <div className="client-opt-in-out cfix">
                <div className="cb-item">
                  <span className="span-text">{t('composite.clientProfile.optedIn')}</span>
                  <div className="checkbox-box-custom-wrap chk-nophone">
                    <input
                      type="checkbox"
                      className="checkbox-box-custom"
                      id="clients-hide"
                      checked={!requestedOptOut}
                      onChange={handleToggleMarketing}
                    />
                    <label className="checkbox-box-label" htmlFor="clients-hide">
                      <div className="checkbox-box-custom-icon">
                        <span className="ic-sml-18px ic_check_white_18" />
                      </div>
                    </label>
                  </div>
                </div>
                <div className="cb-item">
                  <span className="span-text">{t('composite.clientProfile.optedOut')}</span>
                  <div className="checkbox-box-custom-wrap chk-nophone">
                    <input
                      type="checkbox"
                      className="checkbox-box-custom"
                      id="clients-hide"
                      checked={requestedOptOut}
                      onChange={handleToggleMarketing}
                    />
                    <label className="checkbox-box-label" htmlFor="clients-hide">
                      <div className="checkbox-box-custom-icon">
                        <span className="ic-sml-18px ic_check_white_18" />
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <div className={cssCheckForFeatureData} />
      </BWContent>
    </div>
  );
};

export default BasicProfile;
